import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FilterIcon, DownloadIcon, CalendarIcon, ChevronDownIcon, TrendingUpIcon, TrendingDownIcon, HistoryIcon, SearchIcon } from 'lucide-react';
import { useWallet } from '../hooks/useWallet';
import { formatNaira } from '../utils/formatCurrency';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Card } from '../components/ui/Card';
import { Tabs, TabPanel } from '../components/ui/Tabs';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { TransactionList } from '../components/features/TransactionList';
const tabs = [{
  id: 'all',
  label: 'All Transactions'
}, {
  id: 'refunds',
  label: 'Refunds'
}, {
  id: 'bookings',
  label: 'Bookings'
}];
// Stats Summary Card
function StatsSummary({
  totalCredits,
  totalDebits,
  transactionCount
}: {
  totalCredits: number;
  totalDebits: number;
  transactionCount: number;
}) {
  return <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-969">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} data-id="element-970">
        <Card variant="flat" padding="md" className="bg-success-50 border-success-100" data-id="element-971">
          <div className="flex items-center gap-2 mb-2" data-id="element-972">
            <TrendingUpIcon className="w-4 h-4 text-success-600" data-id="element-973" />
            <span className="text-body-sm text-success-700" data-id="element-974">Total Credits</span>
          </div>
          <p className="text-heading-md font-bold text-success-700 tabular-nums" data-id="element-975">
            {formatNaira(totalCredits)}
          </p>
        </Card>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.15
    }} data-id="element-976">
        <Card variant="flat" padding="md" className="bg-error-50 border-error-100" data-id="element-977">
          <div className="flex items-center gap-2 mb-2" data-id="element-978">
            <TrendingDownIcon className="w-4 h-4 text-error-600" data-id="element-979" />
            <span className="text-body-sm text-error-700" data-id="element-980">Total Debits</span>
          </div>
          <p className="text-heading-md font-bold text-error-700 tabular-nums" data-id="element-981">
            {formatNaira(totalDebits)}
          </p>
        </Card>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-982">
        <Card variant="flat" padding="md" className="bg-primary-50 border-primary-100" data-id="element-983">
          <div className="flex items-center gap-2 mb-2" data-id="element-984">
            <HistoryIcon className="w-4 h-4 text-primary-600" data-id="element-985" />
            <span className="text-body-sm text-primary-700" data-id="element-986">Transactions</span>
          </div>
          <p className="text-heading-md font-bold text-primary-700 tabular-nums" data-id="element-987">
            {transactionCount}
          </p>
        </Card>
      </motion.div>
    </div>;
}
export function HistoryPage() {
  const {
    balance,
    transactions,
    getTransactionsByType,
    getTotalCredits,
    getTotalDebits
  } = useWallet();
  const [activeTab, setActiveTab] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const getFilteredTransactions = () => {
    let filtered = transactions;
    if (activeTab === 'refunds') {
      filtered = getTransactionsByType('credit');
    } else if (activeTab === 'bookings') {
      filtered = getTransactionsByType('debit');
    }
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(t => t.description.toLowerCase().includes(searchQuery.toLowerCase()) || t.id.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return filtered;
  };
  const filteredTransactions = getFilteredTransactions();
  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsExporting(false);
    // In a real app, this would trigger a download
  };
  return <div className="min-h-screen bg-gradient-to-b from-surface-50 via-white to-surface-50" data-id="element-988">
      <Header walletBalance={balance} data-id="element-989" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-990">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-991">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-992">
            <div data-id="element-993">
              <h1 className="text-display-sm text-surface-900 mb-1" data-id="element-994">
                Transaction History
              </h1>
              <p className="text-body-md text-surface-500" data-id="element-995">
                View and manage all your wallet transactions
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-996">
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-997">
                <Button variant="ghost" size="sm" icon={<FilterIcon className="w-4 h-4" data-id="element-999" />} onClick={() => setShowFilters(!showFilters)} className={showFilters ? 'bg-primary-50 text-primary-600' : ''} data-id="element-998">
                  Filters
                  <motion.span animate={{
                  rotate: showFilters ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1000">
                    <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-1001" />
                  </motion.span>
                </Button>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1002">
                <Button variant="secondary" size="sm" icon={<DownloadIcon className="w-4 h-4" data-id="element-1004" />} onClick={handleExport} loading={isExporting} data-id="element-1003">
                  Export
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Stats Summary */}
          <StatsSummary totalCredits={getTotalCredits()} totalDebits={getTotalDebits()} transactionCount={transactions.length} data-id="element-1005" />

          {/* Filters */}
          <AnimatePresence data-id="element-1006">
            {showFilters && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="mb-6 overflow-hidden" data-id="element-1007">
                <Card variant="flat" padding="md" className="bg-surface-50" data-id="element-1008">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1009">
                    <Input label="Search" placeholder="Search transactions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} leftIcon={<SearchIcon className="w-4 h-4" data-id="element-1011" />} data-id="element-1010" />
                    <Input label="From Date" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1013" />} data-id="element-1012" />
                    <Input label="To Date" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-1015" />} data-id="element-1014" />
                  </div>
                  {(searchQuery || dateFrom || dateTo) && <motion.div className="mt-4 pt-4 border-t border-surface-200" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} data-id="element-1016">
                      <Button variant="ghost" size="sm" onClick={() => {
                  setSearchQuery('');
                  setDateFrom('');
                  setDateTo('');
                }} data-id="element-1017">
                        Clear Filters
                      </Button>
                    </motion.div>}
                </Card>
              </motion.div>}
          </AnimatePresence>

          {/* Tabs & Content */}
          <Card variant="elevated" padding="none" className="shadow-xl border-0" data-id="element-1018">
            <div className="p-4 border-b border-surface-100" data-id="element-1019">
              <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-1020" />
            </div>

            <div className="p-6" data-id="element-1021">
              <AnimatePresence mode="wait" data-id="element-1022">
                <motion.div key={activeTab} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-1023">
                  {filteredTransactions.length > 0 ? <TransactionList transactions={filteredTransactions} data-id="element-1024" /> : <div className="text-center py-12" data-id="element-1025">
                      <motion.div className="w-16 h-16 mx-auto mb-4 bg-surface-100 rounded-full flex items-center justify-center" animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity
                  }} data-id="element-1026">
                        <HistoryIcon className="w-8 h-8 text-surface-400" data-id="element-1027" />
                      </motion.div>
                      <h3 className="text-heading-md text-surface-900 mb-2" data-id="element-1028">
                        No transactions found
                      </h3>
                      <p className="text-body-md text-surface-500" data-id="element-1029">
                        {searchQuery ? 'Try adjusting your search or filters' : 'Your transaction history will appear here'}
                      </p>
                    </div>}
                </motion.div>
              </AnimatePresence>
            </div>
          </Card>

          {/* Summary */}
          {filteredTransactions.length > 0 && <motion.div className="mt-6 text-center" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3
        }} data-id="element-1030">
              <p className="text-body-sm text-surface-500" data-id="element-1031">
                Showing{' '}
                <span className="font-semibold text-surface-700" data-id="element-1032">
                  {filteredTransactions.length}
                </span>{' '}
                transaction
                {filteredTransactions.length !== 1 ? 's' : ''}
                {activeTab !== 'all' && <span data-id="element-1033">
                    {' '}
                    in{' '}
                    <span className="font-semibold text-surface-700" data-id="element-1034">
                      {activeTab}
                    </span>
                  </span>}
              </p>
            </motion.div>}
        </motion.div>
      </main>

      <Footer data-id="element-1035" />
    </div>;
}